export const Training_SOP = {
    trainingPhilosophyTools: {
        title: "Training Philosophy & Approved Tools",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingPhilosophyToolsTitle.png",
        body: `
          <section>
            <h3>🧠 Positive, Patient, Pawsible: Our Training Philosophy</h3>
            <p>
              At Pawsible, <span class="highlight">our training philosophy is built on science, trust, and emotional intelligence</span>. 
              We use only <span class="highlight-green">positive reinforcement, fear-free methods, and relationship-centered strategies</span> to help dogs learn, adapt, and thrive.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Core Pawsible Training Principles</h4>
            <ul>
              <li>🐾 Reward desirable behaviors clearly, immediately, and consistently</li>
              <li>🛡️ Manage the environment to prevent unwanted behaviors rather than punish them</li>
              <li>🎯 Focus on building skills, not correcting mistakes</li>
              <li>🧠 Use patience, repetition, and progressive challenge to foster true understanding</li>
              <li>🤝 Prioritize the emotional wellbeing of the dog at every step of training</li>
            </ul>
            <p><span class="highlight-blue">We don't train through intimidation or control — we coach through connection and clarity.</span></p>
          </section>
      
          <section>
            <h4>🛠️ 2. Approved Training Tools</h4>
            <ul>
              <li>✅ Treats, kibbles, toys, high-value items</li>
              <li>✅ Verbal praise and physical affection (as tolerated by the dog)</li>
              <li>✅ Marker words (“Yes! or Good!”) or clicker training</li>
              <li>✅ Front-clip harnesses for loose leash walking training</li>
              <li>✅ Flat collars (non-corrective only)</li>
              <li>✅ Snuffle mats, puzzle toys, enrichment tools for engagement and focus</li>
              <li>✅ Target sticks, place mats, and visual stationing aids</li>
              <li>✅ Mock Tables for table turfing training</li>
            </ul>
            <p><span class="highlight-green">Tools should empower dogs to think, choose, and succeed — not force them into submission.</span></p>
          </section>
      
          <section>
            <h4>⛔ 3. Prohibited Training Tools & Methods</h4>
            <ul>
              <li>🚫 No prong collars</li>
              <li>🚫 No choke chains</li>
              <li>🚫 No shock collars (e-collars)</li>
              <li>🚫 No intimidation, yelling, or any punitive methods</li>
              <li>🚫 No alpha rolling, pinning, or forced submission techniques</li>
            </ul>
            <p><span class="highlight-red">Using fear, pain, or intimidation violates Pawsible’s values and will not be tolerated under any circumstances.</span></p>
          </section>
      
          <section>
            <h4>🧩 4. How We Adapt Training to the Individual Dog</h4>
            <ul>
              <li>🧠 Adjust motivation (treats, toys, affection) based on what each dog values most</li>
              <li>📈 Scale difficulty appropriately — start easy, build success gradually</li>
              <li>🛡️ Pause or adjust session pacing if dog shows signs of stress, fear, or frustration</li>
              <li>🌿 Decompress as needed — some dogs need mental breaks to thrive</li>
            </ul>
            <p><span class="highlight-blue">We meet the dog where they are — and help them grow from there with patience and respect.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">At Pawsible, training isn't about controlling dogs — it's about teaching them, building their confidence, and earning their trust.</span> 
              We empower both pets and parents through kindness, clarity, and consistency — every single session.
            </p>
          </section>
        `
      },
      handlerEnergy: {
        title: "Handler Energy & Body Language",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/handlerEnergyTitle.png",
        body: `
          <section>
            <h3>🧘‍♂️ Calm Leadership: Handler Energy & Body Language</h3>
            <p>
              At Pawsible, <span class="highlight">what we communicate silently matters just as much — if not more — than the words we say</span>.
              Dogs read <span class="highlight-green">body language, tone, and emotional energy</span> instantly and mirror the signals we project.
              Calm, confident, and centered handlers create <strong>safe, focused, and cooperative learners</strong>.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Core Energy Principles for Pawsible Trainers</h4>
            <ul>
              <li>🧘‍♂️ Calm, slow, purposeful movements — avoid quick, jerky, rushed gestures</li>
              <li>📣 Soft, encouraging tone — high-energy praise only after successful behaviors</li>
              <li>🧑‍🤝‍🧑 Neutral body posture — no looming, hovering, or leaning aggressively over dogs</li>
              <li>🐾 Open, welcoming stance — relaxed shoulders, soft knees, non-threatening eye contact</li>
              <li>🌿 Emotional neutrality — no visible frustration, anger, or panic even if sessions are challenging</li>
            </ul>
            <p><span class="highlight-blue">You are the thermostat — set the emotional temperature you want the dog to feel.</span></p>
          </section>
      
          <section>
            <h4>🧠 2. Techniques to Regulate Your Energy</h4>
            <ul>
              <li>🌬️ Deep, slow breathing before sessions and during reset moments</li>
              <li>🎯 Focus on process, not perfection — reward small wins to keep tone light and encouraging</li>
              <li>🛑 Take micro-pauses if needed — stepping back, breathing, mentally resetting yourself first</li>
              <li>🧠 Visualize success — imagine the dog succeeding and yourself guiding calmly toward it</li>
            </ul>
            <p><span class="highlight-green">Dogs don't follow verbal instructions — they follow emotional leadership.</span></p>
          </section>
      
          <section>
            <h4>👀 3. Reading Your Own Body Language During Sessions</h4>
            <ul>
              <li>👣 Feet grounded, not constantly shifting weight or pacing nervously</li>
              <li>🖐️ Hands relaxed — not clenched, grabbing, or flailing unpredictably</li>
              <li>🧍‍♂️ Posture upright but soft — no stiff arms crossed or aggressive posturing</li>
              <li>👀 Maintain soft, friendly eye contact (brief, inviting, not staring contests)</li>
            </ul>
            <p><span class="highlight-blue">You are always speaking with your body — make sure it's saying "safe," "steady," and "trustworthy."</span></p>
          </section>
      
          <section>
            <h4>🛡️ 4. Special Situations: Nervous, Fearful, or Reactive Dogs</h4>
            <ul>
              <li>🌱 Shrink your physical presence — turn slightly sideways, kneel if appropriate, avoid direct approach</li>
              <li>🐾 Let the dog initiate interaction when ready — no reaching over or grabbing attempts</li>
              <li>🌸 Use breath and soft posture to de-escalate rather than adding excitement or pressure</li>
              <li>🧘‍♂️ Move like "walking through water" — slow, fluid, predictable motions that lower perceived threat</li>
            </ul>
            <p><span class="highlight-green">Your presence alone should help sensitive dogs feel safer — before you ever say a word or offer a treat.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">At Pawsible, we don't just train dogs — we inspire trust through who we are, how we move, and how we lead.</span> 
              Calm handlers create confident learners. Every session starts with the energy you bring into it.
            </p>
          </section>
        `
      },      
      trainingClientIntake: {
        title: "Intake & Client Communication",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/departureStatusAppUpdateTitle.png",
        body: `
          <section>
            <h3>🎯 Clear Beginnings: Training Intake & Client Communication</h3>
            <p>
              At Pawsible, <span class="highlight">every great training journey starts with clarity, trust, and realistic expectations</span>. 
              Our Training Intake process ensures <span class="highlight-green">we set up both the dog and the parent for long-term success</span> — before the first session even begins.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Training Intake Process</h4>
            <ul>
              <li>📝 Collect client information (dog profile, behavior questionnaire, training goals)</li>
              <li>👂 Conduct a short intake conversation: listen carefully to client concerns, desires, and goals</li>
              <li>🧠 Review dog's age, breed traits, history (past training, behavior notes, any bite history, medical alerts)</li>
              <li>📲 Confirm service type booked (private session, puppy class, group class, specialty training)</li>
            </ul>
            <p><span class="highlight-blue">The more we know upfront, the smarter and safer our training plans will be.</span></p>
          </section>
      
          <section>
            <h4>🧠 2. Setting Client Expectations</h4>
            <ul>
              <li>🔄 Explain the positive reinforcement-based methods we use (no harsh corrections, no dominance training)</li>
              <li>📆 Clarify realistic timeframes for behavior change (weeks, not one-session fixes)</li>
              <li>🏠 Emphasize the need for consistent practice at home (homework is critical!)</li>
              <li>🧘‍♂️ Normalize setbacks — progress isn’t perfectly linear, and patience is part of the process</li>
              <li>📋 Discuss policies: cancellations, reschedules, payment expectations, follow-up communications</li>
            </ul>
            <p><span class="highlight-green">Setting boundaries early builds trust, reduces client anxiety, and increases long-term satisfaction.</span></p>
          </section>
      
          <section>
            <h4>📲 3. How to Communicate Training Plans and Updates</h4>
            <ul>
              <li>📋 Provide a summary training plan in training tasks board (general goals, milestones, approach)</li>
              <li>💬 Keep communications positive, encouraging, and realistic (no overpromising)</li>
              <li>📲 Offer clear channels for follow-up questions (done in tasks board under "Training" and client gets text/email)</li>
              <li>🎯 Encourage clients to celebrate small wins during training journey, not just end goals</li>
            </ul>
            <p><span class="highlight-blue">Professional communication makes training feel like a supported partnership — not a transaction.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Training starts before the first cue is taught.</span> 
              At Pawsible, intake conversations build trust, strengthen understanding, and lay the emotional foundation for every success to follow.
            </p>
          </section>
        `
      },
      trainingSessionAndSpacePrep: {
        title: "Session & Space Prep for Training Success",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingSessionAndSpacePrepTitle.png",
        body: `
          <section>
            <h3>🧘 Calm Space, Clear Intent: Prep Is the Foundation of Great Training</h3>
            <p>
              At Pawsible, we don’t just show up and wing it — <span class="highlight">we prepare with purpose</span>. 
              From the room setup to the session strategy, everything we do before the leash is clipped on shapes how well dogs — and humans — learn.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Session Planning & Mental Readiness</h4>
            <ul>
              <li>📲 Review the dog’s profile and training goals on the app or dashboard</li>
              <li>🎯 Set 2–3 achievable goals for this session — clarity beats complexity</li>
              <li>😌 Center yourself: your energy sets the tone (calm > chaotic)</li>
              <li>🗣️ Prep your coaching voice: encouraging, grounded, and clear</li>
              <li>🧠 Anticipate dog-specific needs: motivation, fears, pace, and handler role</li>
            </ul>
            <p><span class="highlight-blue">When the trainer is focused and present, the session feels safe and productive for all species.</span></p>
          </section>
      
          <section>
            <h4>🏫 2. Training Space Setup</h4>
            <ul>
              <li>🧼 Sanitize mats, cones, props, and any high-touch items</li>
              <li>📍 Mark dog stations or mat areas to create structure and space</li>
              <li>🚪 Secure all gates, pens, and exits — no surprise escapes</li>
              <li>🧺 Set up treat stations, water bowls, trash bins, and sanitizer</li>
              <li>🧘 Create flow: avoid clutter, distractions, and visual chaos</li>
            </ul>
            <p><span class="highlight-green">A clean, safe, organized space builds trust — before the first cue is ever given.</span></p>
          </section>
      
          <section>
            <h4>🧰 3. Tools & Materials Checklist</h4>
            <ul>
              <li>✅ High-value treats and treat pouch</li>
              <li>✅ Clicker (if used), long line, target stick, or mat</li>
              <li>✅ Clean backup leash and collar/harness</li>
              <li>✅ Any props (tunnels, cones, chairs, visual targets)</li>
              <li>✅ Printout or digital access to training plan</li>
            </ul>
            <p><span class="highlight-blue">Don’t improvise on tools — arrive ready and in control.</span></p>
          </section>
      
          <section>
            <h4>🤝 4. Setting Up the Human Learner</h4>
            <ul>
              <li>👂 Ask for updates from the last session (listen calmly, no judgment)</li>
              <li>🎯 Outline today’s goals clearly — no jargon</li>
              <li>🪞 Coach observation first, then participation (step-by-step)</li>
              <li>📸 Let them know you may capture photos/videos for coaching or records</li>
            </ul>
            <p><span class="highlight-green">Training success includes the client — make sure they’re prepped and confident too.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Preparation is your first cue.</span>  
              At Pawsible, every detail — from water bowls to warmups — reflects our commitment to calm, confident, connected learning.
            </p>
          </section>
        `
      },
      privateSessionSOP: {
        title: "Private Session SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/privateSessionSOPTitle.png",
        body: `
          <section>
            <h3>🐾 One-on-One Impact: Private Session SOP</h3>
            <p>
              At Pawsible, private training sessions are tailored to <span class="highlight">individual dog needs, emotional safety, and goal-focused learning</span>. 
              These sessions create space to work on specific behaviors without the stimulation of a group setting.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Purpose of Private Sessions</h4>
            <ul>
              <li>💬 Address individualized goals that don’t suit a group class setting</li>
              <li>🧠 Work through behavioral challenges with more control and fewer distractions</li>
              <li>👥 Build trust and communication between the trainer, dog, and paw parent</li>
            </ul>
            <p><span class="highlight-blue">Private sessions are powerful for tackling specific goals, emotional needs, or reactive behavior with calm, tailored attention.</span></p>
          </section>
      
          <section>
            <h4>📝 2. Before the Session</h4>
            <ul>
              <li>📲 Review the dog’s profile, goals, and past session history in the app</li>
              <li>📋 Prepare the session plan with 2–3 realistic focus points</li>
              <li>🧰 Set up the space with all needed gear (treats, targets, backup leashes, safety mats)</li>
              <li>🗣️ Greet the client warmly, confirm today's goals and expected outcomes</li>
            </ul>
            <p><span class="highlight-green">Clarity at the start helps manage expectations and ensures mutual focus from the beginning.</span></p>
          </section>
      
          <section>
            <h4>🐕 3. Session Flow</h4>
            <ul>
              <li>🧘 Start slow — gauge dog’s comfort and energy level before beginning formal training</li>
              <li>🧠 Mix training, short breaks, and emotional check-ins for optimal engagement</li>
              <li>📣 Narrate your actions gently to guide both dog and parent (“We’re rewarding calm focus here… great job, Max!”)</li>
              <li>🔄 Adjust techniques live based on dog’s body language or emotional state</li>
              <li>🎯 End on a successful, confidence-building win — never push to frustration</li>
            </ul>
            <p><span class="highlight-blue">The goal is learning with trust — not perfection in a single session.</span></p>
          </section>
      
          <section>
            <h4>📋 4. Wrapping Up & Parent Communication</h4>
            <ul>
              <li>💬 Recap key takeaways and progress in simple, actionable language</li>
              <li>📝 Share 1–2 “homework” items for continued practice at home</li>
              <li>📅 Confirm if a follow-up is needed — and set expectations</li>
              <li>📲 Log session notes in the app immediately for team-wide access</li>
            </ul>
            <p><span class="highlight-green">Your calm, thoughtful debrief helps the client stay motivated and feel supported.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Private training is a relationship-building service</span>, not just a skills class.
              Take your time, read the dog, and coach the parent with patience and clarity. At Pawsible, <strong>we train minds, not just manners.</strong>
            </p>
          </section>
        `
      },
      groupClassSOP: {
        title: "Group Class SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/groupClassSOPTitle.png",
        body: `
          <section>
            <h3>👥 Learning Together: Group Class SOP</h3>
            <p>
              Group classes at Pawsible offer dogs and paw parents the chance to learn in a <span class="highlight-green">social, structured, and supportive setting</span>. 
              These sessions are designed to <span class="highlight">strengthen foundational skills, improve focus around distractions, and build real-world confidence</span>.
            </p>
          </section>
      
          <section>
            <h4>🧭 1. Class Goals & Structure</h4>
            <ul>
              <li>🎯 Reinforce basic manners, obedience, and responsiveness in the presence of other dogs</li>
              <li>🐾 Build polite social behavior, impulse control, and attention to handler</li>
              <li>👫 Foster collaboration and confidence between owners and dogs</li>
            </ul>
            <p><span class="highlight-blue">Each class should progress in clarity, confidence, and calmness — not just skills alone.</span></p>
          </section>
      
          <section>
            <h4>📋 2. Pre-Class Setup</h4>
            <ul>
              <li>🧼 Ensure the classroom is sanitized, clean, and safely staged (gates closed, mats placed)</li>
              <li>📍 Assign station markers (mats, cones) with appropriate distance between dogs</li>
              <li>✅ Check equipment: clickers, target sticks, extra leashes/collars, clean water bowls</li>
              <li>🗂️ Review class roster and notes on dogs with special needs or behavioral alerts</li>
            </ul>
            <p><span class="highlight-green">Proactive setup ensures a safe and focused learning space — with minimal surprises.</span></p>
          </section>
      
          <section>
            <h4>📣 3. Running the Class</h4>
            <ul>
              <li>🧘 Start with a calm opening — light greetings, intro activity, or focus exercise</li>
              <li>🧠 Teach 1–2 new skills with structured demos, then practice rounds</li>
              <li>🔄 Rotate between active engagement and low-arousal breaks</li>
              <li>👀 Watch for stress signs, reactivity, or over-arousal — offer space as needed</li>
              <li>🤝 Celebrate small wins vocally — dogs feed off energy and encouragement</li>
            </ul>
            <p><span class="highlight-blue">Keep energy grounded and adaptable — your pacing sets the tone.</span></p>
          </section>
      
          <section>
            <h4>💬 4. Engaging the Clients</h4>
            <ul>
              <li>👂 Encourage questions and check understanding throughout the session</li>
              <li>📸 Provide live feedback gently and clearly (e.g., “That was a great timing on your marker!”)</li>
              <li>📓 Summarize at the end with 2–3 key takeaways and optional homework</li>
              <li>📲 Log general class notes or flag client-specific feedback in the dashboard afterward</li>
            </ul>
            <p><span class="highlight-green">Clients leave feeling educated, empowered, and proud of their dog's progress.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Group classes aren’t just for obedience — they build community, confidence, and joy.</span>  
              At Pawsible, we set each team (human and dog) up to thrive — calmly, patiently, and with a smile.
            </p>
          </section>
        `
      },
      puppyClassSOP: {
        title: "Puppy Class SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/puppyClassSOPTitle.png",
        body: `
          <section>
            <h3>🍼 Welcome to Puppy Class: Building Confidence From the Ground Up</h3>
            <p>
              Pawsible’s Puppy Class is all about <span class="highlight">safe socialization, basic life skills, and confidence-building</span> during a dog’s most critical development window. 
              Our role is to <strong>guide young pups gently, reinforce curiosity, and prevent future fear-based behaviors</strong> — all while educating first-time paw parents.
            </p>
          </section>
      
          <section>
            <h4>🎯 1. Puppy Class Goals</h4>
            <ul>
              <li>🐶 Safely expose puppies to new sights, sounds, surfaces, and social situations</li>
              <li>🧠 Introduce basic cues like sit, touch, name recall, and polite greetings</li>
              <li>👂 Teach paw parents foundational dog behavior and positive reinforcement principles</li>
              <li>❤️ Prevent problem behaviors by shaping trust and emotional regulation early</li>
            </ul>
            <p><span class="highlight-blue">The focus is confidence > control — we grow calm, curious learners first.</span></p>
          </section>
      
          <section>
            <h4>🧺 2. Pre-Class Setup</h4>
            <ul>
              <li>🧼 Sanitize surfaces, remove hazards, and prepare for occasional accidents</li>
              <li>🧸 Set up soft mats, puppy gates, low platforms, tunnels, or novel textures</li>
              <li>🥎 Provide safe toys for structured play moments</li>
              <li>📍 Ensure each puppy has space and a clear visual “station”</li>
              <li>📋 Review roster and age/stage of all attendees</li>
            </ul>
            <p><span class="highlight-green">The environment must be soft, safe, and clearly structured for tiny paws and short attention spans.</span></p>
          </section>
      
          <section>
            <h4>📚 3. Class Structure</h4>
            <ul>
              <li>⏳ Sessions are shorter: ~30–40 minutes of work/play time total</li>
              <li>🧘 Start with calm mat work or gentle name recall</li>
              <li>🎯 Teach 1 cue at a time with fun repetitions (e.g., sit, touch, name game)</li>
              <li>🛝 Include exploration time with safe novelty (wobble boards, plastic grates, snuffle mats)</li>
              <li>🐾 Supervise light play sessions — group size and temperament should be matched carefully</li>
            </ul>
            <p><span class="highlight-blue">Keep everything short, successful, and fun — failure or fear has no place in early learning.</span></p>
          </section>
      
          <section>
            <h4>💬 4. Parent Communication</h4>
            <ul>
              <li>🎓 Explain the “why” behind each activity — it helps shape long-term training mindset</li>
              <li>📓 Offer take-home tips and short daily practice tasks</li>
              <li>📸 Share wins: “Luna explored the wobble board today — such bravery!”</li>
              <li>📲 Follow up on concerns (e.g., fear periods, rough play) via client dashboard</li>
            </ul>
            <p><span class="highlight-green">We’re not just training puppies — we’re mentoring new pet parents, too.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Puppy class lays the emotional and behavioral foundation for life.</span>  
              Our calm, joyful, and intentional guidance helps shape confident dogs — and confident humans — from day one.
            </p>
          </section>
        `
      },
      trainingProgressTracking: {
        title: "Progress Tracking & Homework Notes",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingProgressTrackingTitle.png",
        body: `
          <section>
            <h3>📈 Turning Lessons into Long-Term Success</h3>
            <p>
              At Pawsible, training doesn’t end when the session does. <span class="highlight">Tracking progress and providing homework ensures consistency, structure, and real-world results</span> — with full transparency for pet parents.
            </p>
          </section>
      
          <section>
            <h4>📋 1. What to Track After Each Session</h4>
            <ul>
              <li>🎯 <strong>Cues introduced:</strong> Sit, Place, Recall, etc.</li>
              <li>📶 <strong>Proficiency level:</strong> Just introduced, emerging, reliable, fluent</li>
              <li>🧠 <strong>Dog’s learning style & motivators:</strong> Treats, toys, praise, handler movement</li>
              <li>🔍 <strong>Challenges observed:</strong> Reactivity, distraction, energy dips, stress signs</li>
            </ul>
            <p><span class="highlight-blue">Progress notes help us individualize training and plan each session strategically.</span></p>
          </section>
      
          <section>
            <h4>📝 2. Writing Clear Homework Notes</h4>
            <ul>
              <li>🗂️ Keep it simple and doable (1–2 focus areas max)</li>
              <li>📦 Use real-life scenarios: “Practice ‘Place’ before meals and when guests arrive”</li>
              <li>🧠 Reinforce the “why” — explain what the exercise builds (confidence, recall, calm)</li>
              <li>📲 Enter homework into the training dashboard or include in the post-session message</li>
            </ul>
            <p><span class="highlight-green">Homework is a bridge between sessions — make it clear, specific, and supportive.</span></p>
          </section>
      
          <section>
            <h4>🔄 3. Updating the Training Profile</h4>
            <ul>
              <li>✅ Update each cue’s status: Not Introduced → In Progress → Fluent</li>
              <li>🧠 Add notes on triggers, breakthroughs, and motivators</li>
              <li>📌 Log notes on family dynamics if relevant (e.g., “Dog listens better to adult male at home”)</li>
              <li>🛡️ If behavioral challenges arise, flag for lead trainer review</li>
            </ul>
            <p><span class="highlight-blue">Up-to-date training profiles empower all staff to stay aligned on progress and needs.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Training is a relationship, not a transaction.</span> 
              Documenting wins, setbacks, and insights creates continuity — and builds client trust with every update.
            </p>
          </section>
        `
      },
      behaviorEscalation: {
        title: "Behavior Issue Escalation Protocol",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/behaviorEscalationTitle.png",
        body: `
          <section>
            <h3>🚩 Stay Grounded, Stay In Control</h3>
            <p>
              At Pawsible, we believe <span class="highlight">challenging behaviors are learning opportunities</span> — not emergencies. This SOP equips staff to confidently respond, document, and de-escalate before involving a supervisor.
            </p>
          </section>
      
          <section>
            <h4>👀 1. What to Watch For</h4>
            <ul>
              <li>🗯️ Sudden growling, barking, or lunging at dogs or staff</li>
              <li>🛑 Resource guarding behaviors (e.g., tense body, hovering over toy or bowl)</li>
              <li>🐕 Excessive fixation, stalking, or chasing behavior that doesn’t disengage</li>
              <li>😰 Freeze or flight behavior in response to mild stimuli</li>
              <li>⚠️ Snapping, muzzle punches, or redirection frustration</li>
            </ul>
            <p><span class="highlight-red">Early signs matter — the sooner you respond, the easier it is to redirect safely.</span></p>
          </section>
      
          <section>
            <h4>🛠️ 2. First Response Steps (Try Before Escalating)</h4>
            <ul>
              <li>📦 <strong>Give space:</strong> Move the dog to a calmer area or away from triggers</li>
              <li>🔄 <strong>Reset the moment:</strong> Offer a simple, known cue ("touch," "look") to redirect focus</li>
              <li>🧘 <strong>Lower your own energy:</strong> Use calm, non-reactive body language and tone</li>
              <li>🎯 <strong>Reinforce desired behavior:</strong> Praise or reward any calming signals (sniffing, turning away, sitting)</li>
              <li>🪷 <strong>Give downtime:</strong> If the dog can’t reset, move to a decompression zone and end session gracefully</li>
            </ul>
            <p><span class="highlight-green">You are empowered to guide behavior gently and intentionally — not just report it.</span></p>
          </section>
      
          <section>
            <h4>📋 3. How to Document Behavior Events</h4>
            <ul>
              <li>📝 Log observations into the training dashboard or shift notes</li>
              <li>🔍 Be specific: what happened, what triggered it, and how you responded</li>
              <li>🧠 Note patterns (same environment, handler, time of day?)</li>
              <li>📸 (Optional) Capture video or photo only if it adds helpful context and is safe to do</li>
            </ul>
            <p><span class="highlight-blue">Clear documentation supports smarter training adjustments across the team.</span></p>
          </section>
      
          <section>
            <h4>🚨 4. When to Loop In a Supervisor</h4>
            <ul>
              <li>📛 Behavior continues across multiple sessions with no improvement</li>
              <li>🩹 There’s a risk of harm to other dogs, humans, or the dog themself</li>
              <li>📞 A parent must be informed due to injury, aggression, or training stall</li>
              <li>🧭 You feel out of depth and need guidance (this is a strength, not a failure)</li>
            </ul>
            <p><span class="highlight-yellow">Escalation is about support — not blame. Use it when safety, clarity, or teamwork calls for it.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Behavior issues are not setbacks — they’re signposts.</span>  
              With the right response and clear communication, you can turn tough moments into trust-building breakthroughs.
            </p>
          </section>
        `
      },
      clientTrainingMaterials: {
        title: "Client Training Materials Distribution",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/clientTrainingMaterialsTitle.png",
        body: `
          <section>
            <h3>📚 Equip Clients for Success — On and Off Site</h3>
            <p>
              At Pawsible, <span class="highlight">we don’t just train dogs — we empower humans</span>. 
              Thoughtful, well-timed training materials give clients structure, confidence, and a way to stay consistent between sessions.
            </p>
          </section>
      
          <section>
            <h4>📋 1. What to Share With Clients</h4>
            <ul>
              <li>📄 Digital handouts for each training phase (puppy, basic obedience, behavior tips)</li>
              <li>🎯 Homework checklists aligned with cues covered in the session</li>
              <li>📹 Short tutorial videos or enrichment demos (via app or secure link)</li>
              <li>💡 FAQs and reinforcement cheat sheets (“How to fade the treat,” “Why 'sit' doesn’t stick”)</li>
            </ul>
            <p><span class="highlight-blue">Our materials reflect our standards — clean, clear, science-backed, and brand-aligned.</span></p>
          </section>
      
          <section>
            <h4>🕒 2. When to Share Materials</h4>
            <ul>
              <li>📲 After intake: Puppy prep guides, equipment recommendations</li>
              <li>📅 After each session: Cue reinforcement notes + homework PDF</li>
              <li>🔄 Between sessions: Progress check-in with optional refreshers</li>
              <li>🎉 At graduation: Printable certificate + next step training roadmap</li>
            </ul>
            <p><span class="highlight-green">Timely delivery keeps pet parents engaged and builds momentum through each phase.</span></p>
          </section>
      
          <section>
            <h4>📲 3. How to Deliver Materials</h4>
            <ul>
              <li>📥 Upload to the client profile in the Pawsible training dashboard</li>
              <li>💬 Include a quick summary message or note to reinforce usage</li>
              <li>📧 (Optional) Email PDF packet for non-app users</li>
              <li>📎 Ensure materials are mobile-friendly and printable</li>
            </ul>
            <p><span class="highlight-blue">Consistency in distribution ensures every client receives the full Pawsible experience — not just those who ask for it.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Information is care.</span> 
              The more clients understand the training process, the more likely they are to stick with it — and celebrate it.
            </p>
          </section>
        `
      },
      trainingFollowUp: {
        title: "Follow-Up Expectations",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/trainingFollowUpTitle.png",
        body: `
          <section>
            <h3>🔁 The Real Work Begins Between Sessions</h3>
            <p>
              Training doesn’t stick without follow-through. At Pawsible, we set clear expectations for follow-up so <span class="highlight">clients feel supported, accountable, and empowered</span> — never lost or overwhelmed.
            </p>
          </section>
      
          <section>
            <h4>📝 1. What to Communicate Post-Session</h4>
            <ul>
              <li>📋 Summary of cues covered and behavioral focus areas</li>
              <li>🎯 Homework instructions (max 2 clear tasks)</li>
              <li>💡 Encouragement and progress reflection</li>
              <li>📲 Reminder on when/how the trainer will follow up</li>
            </ul>
            <p><span class="highlight-blue">Consistency and clarity build trust — even after the leash is off.</span></p>
          </section>
      
          <section>
            <h4>📅 2. Follow-Up Timeline Standards</h4>
            <ul>
              <li>📆 <strong>1–2 days after session:</strong> Send post-session summary & homework</li>
              <li>💬 <strong>Midweek check-in:</strong> Quick message to see how training is going</li>
              <li>📸 <strong>Optional:</strong> Ask for video submissions to assess form or progress</li>
              <li>📌 <strong>Before next session:</strong> Revisit progress notes to tailor the plan</li>
            </ul>
            <p><span class="highlight-green">Thoughtful follow-ups show we care — and help clients stay consistent between visits.</span></p>
          </section>
      
          <section>
            <h4>📲 3. How to Log Follow-Up Interactions</h4>
            <ul>
              <li>🗂️ Use the client’s training dashboard to record outreach and updates</li>
              <li>✅ Note responses, progress, or any flags needing review</li>
              <li>🔁 Align with other trainers if part of a multi-trainer program</li>
              <li>📌 Document missed follow-ups for continuity and accountability</li>
            </ul>
            <p><span class="highlight-blue">A documented trail helps us adjust and respond to every dog’s evolving needs.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Our commitment doesn’t end with the session — it extends into the dog’s everyday life.</span> 
              Follow-up is where trust deepens, outcomes solidify, and clients become lifelong believers in the Pawsible way.
            </p>
          </section>
        `
      }      
}